import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DeleteIcon, EditIcon } from 'assets/icons';
import { ContactButton } from 'components/UI/Buttons';
import { type FC, useState } from 'react';
import { useDeleteSectionMutation, useUpdateSectionMutation } from 'services/api/reports.api';
import type { ReportSection } from 'services/types/report';
import { NAVY, RADIUS_CARD, RADIUS_COIN, RADIUS_ITEM, SHADOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';
import textToReactNodes from 'utils/textToReactNodes';

interface Props {
  section: ReportSection;
  reportId: number;
  isEditMode: boolean;
}

const cardSx = {
  p: 3,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  bgcolor: SURFACE_CONTAINER_LOWEST,
  borderRadius: RADIUS_CARD,
  boxShadow: SHADOW,
} as const;

const hiddenCardSx = {
  ...cardSx,
  opacity: 0.5,
  border: `2px dashed ${alpha(NAVY, 0.2)}`,
} as const;

const editBtnSx = {
  color: alpha(NAVY, 0.55),
  transition: 'all 0.15s ease-in-out',
  '&:hover': { bgcolor: alpha(NAVY, 0.06) },
} as const;

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: RADIUS_ITEM,
    '& fieldset': { borderColor: alpha(NAVY, 0.12) },
    '&:hover fieldset': { borderColor: alpha(NAVY, 0.25) },
  },
} as const;

const ConversationSection: FC<Props> = ({ section, reportId, isEditMode }: Props) => {
  const [updateSection] = useUpdateSectionMutation();
  const [deleteSection] = useDeleteSectionMutation();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTitle, setEditTitle] = useState(section.title || '');
  const [editReason, setEditReason] = useState(section.reason || '');
  const [editWidth, setEditWidth] = useState(section.width);
  const [editHeight, setEditHeight] = useState(section.height);

  const handleOpenEdit = () => {
    setEditTitle(section.title || '');
    setEditReason(section.reason || '');
    setEditWidth(section.width);
    setEditHeight(section.height);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    updateSection({
      reportId,
      sectionId: section.id,
      title: editTitle || null,
      reason: editReason || null,
      width: editWidth,
      height: editHeight,
    })
      .unwrap()
      .then(() => setEditDialogOpen(false))
      .catch(console.error);
  };

  const handleToggleVisibility = () => {
    updateSection({ reportId, sectionId: section.id, isHidden: !section.isHidden }).catch(console.error);
  };

  const handleDelete = () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta sección?')) return;
    deleteSection({ reportId, sectionId: section.id }).catch(console.error);
  };

  return (
    <Box sx={section.isHidden ? hiddenCardSx : cardSx}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: alpha(NAVY, 0.08), color: NAVY, width: 32, height: 32 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600, color: NAVY }}>
            {textToReactNodes(section.title || 'Conversación Destacada')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {isEditMode && (
            <>
              <Tooltip title={section.isHidden ? 'Mostrar sección' : 'Ocultar sección'}>
                <IconButton size="small" onClick={handleToggleVisibility} sx={editBtnSx}>
                  {section.isHidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Editar sección">
                <IconButton size="small" onClick={handleOpenEdit} sx={editBtnSx}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Eliminar sección">
                <IconButton
                  size="small"
                  onClick={handleDelete}
                  sx={{ ...editBtnSx, '&:hover': { bgcolor: 'error.light', color: 'error.dark' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {section.clientId && (
            <Tooltip title="Ver cliente completo">
              <IconButton
                size="small"
                component="a"
                href={`/clients/${section.clientId}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={editBtnSx}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Reason */}
      {section.reason && (
        <Box sx={{ bgcolor: 'info.light', borderRadius: RADIUS_ITEM, p: 1.5, mb: 2 }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'info.contrastText' }}>
            💡 {section.reason}
          </Typography>
        </Box>
      )}

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          maxHeight: 350,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          bgcolor: '#e5ddd5',
          borderRadius: RADIUS_ITEM,
          p: 1.5,
        }}
      >
        {section.messages?.map((message, index) => {
          const isUser = message.role === 'user';
          return (
            <Box
              key={`${message.role}-${index + 1}-${message.content.substring(0, 20)}`}
              sx={{ display: 'flex', justifyContent: isUser ? 'flex-start' : 'flex-end' }}
            >
              <Box
                sx={{
                  maxWidth: '80%',
                  bgcolor: isUser ? '#ffffff' : '#d9fdd3',
                  borderRadius: RADIUS_ITEM,
                  px: 1.5,
                  py: 1,
                  boxShadow: '0 1px 0.5px rgba(11,20,26,.13)',
                }}
              >
                <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.875rem', color: '#111b21' }}>
                  {textToReactNodes(message.content)}
                </Box>
                {message.imageUrl && (
                  <Box
                    component="img"
                    src={message.imageUrl}
                    alt="Imagen del mensaje"
                    sx={{ maxWidth: '100%', maxHeight: 150, borderRadius: RADIUS_ITEM, mt: 1 }}
                  />
                )}
              </Box>
            </Box>
          );
        })}
        {(!section.messages || section.messages.length === 0) && (
          <Typography sx={{ color: alpha(NAVY, 0.4), textAlign: 'center', py: 2 }}>
            No hay mensajes para mostrar
          </Typography>
        )}
      </Box>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: NAVY, fontWeight: 600, fontFamily: 'Jost, sans-serif' }}>
          Editar sección de conversación
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Título"
            fullWidth
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            sx={inputSx}
          />
          <TextField
            margin="dense"
            label="Razón"
            fullWidth
            multiline
            rows={3}
            value={editReason}
            onChange={(e) => setEditReason(e.target.value)}
            sx={inputSx}
          />
          <Box sx={{ mt: 3 }}>
            <Typography gutterBottom sx={{ color: alpha(NAVY, 0.6) }}>
              Ancho: {Math.round((editWidth / 12) * 100)}%
            </Typography>
            <Slider
              value={editWidth}
              onChange={(_, v) => setEditWidth(v as number)}
              min={1}
              max={12}
              step={1}
              marks={[
                { value: 3, label: '25%' },
                { value: 6, label: '50%' },
                { value: 9, label: '75%' },
                { value: 12, label: '100%' },
              ]}
              valueLabelDisplay="auto"
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography gutterBottom sx={{ color: alpha(NAVY, 0.6) }}>
              Alto: {editHeight}
            </Typography>
            <Slider
              value={editHeight}
              onChange={(_, v) => setEditHeight(v as number)}
              min={1}
              max={4}
              step={1}
              marks={[
                { value: 1, label: '1' },
                { value: 2, label: '2' },
                { value: 3, label: '3' },
                { value: 4, label: '4' },
              ]}
              valueLabelDisplay="auto"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            sx={{ color: alpha(NAVY, 0.6), borderRadius: RADIUS_COIN, '&:hover': { bgcolor: alpha(NAVY, 0.06) } }}
          >
            Cancelar
          </Button>
          <ContactButton onClick={handleSaveEdit} sx={{ height: 36 }}>
            Guardar
          </ContactButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConversationSection;
